import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import type { PublicMiniApp, MiniAppLaunchScope, LaunchContext } from '@/api/miniApps';
import { miniAppsApi } from '@/api/miniApps';

/**
 * Phase 8 — host iframe shell для мини-аппа.
 *
 * Хост:
 *   1. вызывает `POST /api/mini-apps/:id/launch`, получает signed launchUrl;
 *   2. рендерит iframe с sandbox-флагами и грузит launchUrl;
 *   3. слушает postMessage от iframe, ВСЕГДА сверяя event.source и event.origin
 *      с allowedOrigins мини-апа — иначе отбрасывает;
 *   4. проксирует только UI-методы (toast/confirm/close) сам, остальные
 *      forward'ит мини-апу как ack — фактическая работа делается мини-апом
 *      через bridge HTTP.
 *
 * Хост ничего не знает о E2EE-данных и физически не может их получить —
 * запуск из секретного чата запрещён бэкендом.
 */
export interface MiniAppHostProps {
  app: PublicMiniApp;
  scope: MiniAppLaunchScope;
  context?: LaunchContext;
  onClose: () => void;
}

interface HostMsgFromGuest {
  __irchat?: 'mini-app';
  type: 'ready' | 'toast' | 'confirm' | 'close' | 'log';
  id?: string;
  payload?: { text?: string; level?: 'info' | 'error' };
}

export default function MiniAppHost({ app, scope, context, onClose }: MiniAppHostProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [launchUrl, setLaunchUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; level: 'info' | 'error' } | null>(null);

  const allowedOriginSet = useMemo(() => new Set(app.allowedOrigins), [app.allowedOrigins]);

  // Step 1: prepare a launch session and obtain the signed iframe URL.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLaunchUrl(null);
    miniAppsApi
      .launch(app.id, scope, context ?? {})
      .then((res) => {
        if (cancelled) return;
        setLaunchUrl(res.launchUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = (err?.response?.data?.message as string | undefined) ?? 'Не удалось запустить мини-апп';
        setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [app.id, scope, JSON.stringify(context ?? {})]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2: postMessage listener (UI bridge). Bridge HTTP вызовы мини-апп
  // делает сам — мы видим только высокоуровневые UI-сообщения для тоста
  // и для закрытия.
  useEffect(() => {
    function onMessage(ev: MessageEvent) {
      // Жёсткая origin-проверка. Мы не используем event.source-only check,
      // потому что allowedOrigins — это контракт мини-апа.
      if (!allowedOriginSet.has(ev.origin)) return;
      if (iframeRef.current && ev.source !== iframeRef.current.contentWindow) return;
      const data = ev.data as HostMsgFromGuest | null;
      if (!data || data.__irchat !== 'mini-app') return;

      switch (data.type) {
        case 'ready':
          // Сюда мини-апп отчитывается, что прочитал launch token из location.hash
          // и готов к работе. Хост ничего не делает — соединение установлено.
          break;
        case 'toast': {
          const text = String(data.payload?.text ?? '').slice(0, 200);
          if (text) {
            setToast({ text, level: data.payload?.level === 'error' ? 'error' : 'info' });
            setTimeout(() => setToast(null), 3500);
          }
          break;
        }
        case 'confirm': {
          // Простейшая реализация: window.confirm. Можно заменить на модалку.
          const text = String(data.payload?.text ?? '').slice(0, 400);
          const ok = window.confirm(text || 'Подтвердить?');
          ev.source?.postMessage(
            { __irchat: 'mini-app', type: 'confirm:result', id: data.id, payload: { ok } },
            { targetOrigin: ev.origin },
          );
          break;
        }
        case 'close':
          onClose();
          break;
        case 'log':
          // eslint-disable-next-line no-console
          console.debug('[miniapp]', app.slug, data.payload?.text);
          break;
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [allowedOriginSet, app.slug, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-2 md:p-6">
      <div className="bg-surface-950 border border-surface-800 rounded-xl shadow-2xl flex flex-col w-full max-w-3xl h-[80vh] overflow-hidden">
        <div className="h-12 px-3 flex items-center gap-2 border-b border-surface-800 flex-shrink-0">
          {app.iconUrl && <img src={app.iconUrl} alt="" className="w-6 h-6 rounded" />}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{app.displayName}</div>
            <div className="text-xs text-surface-500 truncate">{app.allowedOrigins[0]}</div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-surface-400 hover:text-surface-100"
            title="Закрыть"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 relative bg-surface-900">
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
              <AlertCircle size={36} className="text-red-400 mb-2" />
              <div className="text-sm text-surface-200 max-w-sm">{error}</div>
            </div>
          )}
          {!error && !launchUrl && (
            <div className="absolute inset-0 flex items-center justify-center text-surface-500">
              <Loader2 size={20} className="animate-spin" />
            </div>
          )}
          {launchUrl && !error && (
            <iframe
              ref={iframeRef}
              src={launchUrl}
              title={app.displayName}
              className="w-full h-full border-0"
              // Sandbox: разрешаем скрипты, формы и popups, но НЕ same-origin —
              // так iframe изолирован от хост-cookies даже если мини-апп
              // лежит на том же домене.
              sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
              referrerPolicy="no-referrer"
              allow="clipboard-write"
            />
          )}

          {toast && (
            <div
              className={`absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-md text-xs shadow-lg ${
                toast.level === 'error'
                  ? 'bg-red-900/90 text-red-100 border border-red-700'
                  : 'bg-surface-800/95 text-surface-100 border border-surface-700'
              }`}
            >
              {toast.text}
            </div>
          )}
        </div>

        <div className="px-3 py-1.5 border-t border-surface-800 text-[10px] text-surface-600 flex-shrink-0">
          Мини-апп от {app.allowedOrigins[0]}. Capabilities:{' '}
          {app.capabilities.length === 0 ? '—' : app.capabilities.join(', ')}
        </div>
      </div>
    </div>
  );
}
